import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import * as Sentry from "@sentry/react"
import { Toaster } from "sonner"
import { MyDispatch, MySelector } from "./redux/store"
import { useEffect } from "react"
import Cookies from "js-cookie"
import { useDispatch } from "react-redux"
import {
  getEventConfig,
  getEventDetails,
  setCategory,
  setEventVerified,
  setPhoneNumber,
  setUserId,
} from "./redux/eventSlice"
import Gallery from "./pages/Gallery"
import AuthModal from "./components/auth/AuthModal"
import Error from "./pages/Error"
import GalleryUnavailable from "./pages/GalleryUnavailable"
import Navbar from "./components/navs/Navbar"
import { safeSessionStorage } from "./utils/safeStorage"
import About from "./pages/About"
import PhysicalAlbum from "./pages/PhysicalAlbum"
import PhysicalAlbumDetail from "./pages/PhysicalAlbumDetail"
import FullScreen from "./pages/FullScreen"
import SelectedPhotosNavbar from "./components/navs/SelectedPhotosNavbar"
import { HandleGalleryFns } from "./hooks/handleGalleryFns"
import {
  resetSelectedPhotos,
  setDownloadEnabled,
  setFullScreenId,
  setShowDownloadModal,
  setAISearchEnabled,
} from "./redux/utilSlice"
import Modals from "./components/modals/Modals"
import BottomBar from "./components/navs/BottomBar"
import downloaderGIF from "./assets/downloader.gif"
import useTheme from "./hooks/useTheme"
import MetaTags from "./components/utils/MetaTags"
import FloatingUploadButton from "./components/utils/FloatingUploadButton"
import FullScreenLoader from "./components/utils/FullScreenLoader"
import IndividualDownloadModal from "./components/modals/IndividualDownloadModal"
import WhatsAppBanner from "./components/utils/WhatsAppBanner"
import { getDesignFontClass } from "./gallery-designs/registry"

// Old /:eventId/auth links now redirect to the gallery, where the auth
// popup blocks access until the user is verified.
const AuthRedirect = () => {
  const { eventId } = useParams()
  return <Navigate to={`/${eventId}/gallery`} replace />
}

// A bare /:eventId link (no sub-path) redirects to the gallery.
const GalleryRedirect = () => {
  const { eventId } = useParams()
  return <Navigate to={`/${eventId}/gallery`} replace />
}

const App = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<MyDispatch>()
  const { data: eventData, error, eventId, config, loading, verified, category } = MySelector((state) => state.event)
  const { allFiles } = MySelector((state) => state.gallery)
  const {
    fullScreenId,
    fullScreen,
    downloadData: { totalParts },
  } = MySelector((state) => state.util)

  const { triggerFullScreen } = HandleGalleryFns()

  useTheme()

  const { userId } = MySelector((state) => state.event)

  useEffect(() => {
    Sentry.setUser(userId ? { id: userId } : null)
  }, [userId])

  useEffect(() => {
    if (!eventId) return
    Sentry.setTag("event_id", eventId)
    Sentry.setTag("category", category)
  }, [eventId, category])

  // to check and set data
  useEffect(() => {
    if (!eventData) return
    if (error) return
    if (eventData !== "null") return

    const routeCheck = location.pathname.split("/")

    if (routeCheck[2] === "file") {
      dispatch(setFullScreenId(routeCheck[3]))
    }

    const cookieData = Cookies.get(
      `${process.env.REACT_APP_IFE_WEB_GALLERY_COOKIE}?EVENT=${routeCheck[1]}` ||
        ""
    )
    if (cookieData) {
      const parsedCookie = JSON.parse(cookieData)
      dispatch(getEventDetails(parsedCookie.eventId))
      dispatch(setPhoneNumber(parsedCookie.number))
      if (parsedCookie.userId) dispatch(setUserId(parsedCookie.userId))
      dispatch(setEventVerified())
      const activeSession = safeSessionStorage.getItem(
        process.env.REACT_APP_ACTIVE_SESSION || ""
      )
      if (activeSession === "host") dispatch(setCategory("host"))
      else if (activeSession === "guest") dispatch(setCategory("guest"))
      else navigate(`/${parsedCookie.eventId}/gallery`)
    } else {
      dispatch(getEventDetails(routeCheck[1]))
      navigate(`/${routeCheck[1]}/gallery`)
    }
  }, [eventData, error, dispatch])

  // to reset selected photos when changing route
  useEffect(() => {
    dispatch(resetSelectedPhotos())
  }, [location.pathname, dispatch])

  // to trigger full screen if authenticated on reload
  useEffect(() => {
    if (!eventId) return
    if (!fullScreenId) return
    if (!allFiles) return
    const isSessionActive = safeSessionStorage.getItem(
      process.env.REACT_APP_ACTIVE_SESSION || ""
    )
    if (fullScreenId && isSessionActive) triggerFullScreen()
  }, [fullScreenId, eventId, allFiles, triggerFullScreen])

  // to enable smooth scrolling
  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth"
  }, [])

  // block right-click / long-press "Save image" menu on photos app-wide
  useEffect(() => {
    const blockImageContextMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target && (target.tagName === "IMG" || target.tagName === "VIDEO")) {
        event.preventDefault()
      }
    }
    document.addEventListener("contextmenu", blockImageContextMenu)
    return () =>
      document.removeEventListener("contextmenu", blockImageContextMenu)
  }, [])

  // scroll to top on page or folder change
  useEffect(() => {
    const scrollableContainer = document.querySelector(
      ".max-h-screen.overflow-y-scroll"
    )
    if (scrollableContainer) {
      scrollableContainer.scrollTop = 0
    }
    window.scrollTo(0, 0)
  }, [location.pathname])

  // to check and set if download enabled
  useEffect(() => {
    if (!config) return
    dispatch(setDownloadEnabled(config?.downloadEnabled || false))
    dispatch(setAISearchEnabled(config?.aiSearchEnabled || false))
  }, [config, dispatch, eventData])

  // to get event config
  useEffect(() => {
    if (!eventId) return
    dispatch(getEventConfig(eventId || ""))
  }, [dispatch, eventId])

  // to set category when reloaded
  useEffect(() => {
    const activeSession = safeSessionStorage.getItem(
      process.env.REACT_APP_ACTIVE_SESSION || ""
    )
    if (activeSession === "host") dispatch(setCategory("host"))
    else if (activeSession === "guest") dispatch(setCategory("guest"))
  }, [dispatch])

  // Everyone enters as a guest once auth is satisfied (or not required).
  // Hosts can switch later via the "Switch to Host" button.
  useEffect(() => {
    if (category) return
    if (config === undefined) return
    if (!config?.dataCollectionEnabled || verified) {
      safeSessionStorage.setItem(process.env.REACT_APP_ACTIVE_SESSION || "", "guest")
      dispatch(setCategory("guest"))
    }
  }, [category, config, verified, dispatch])

  // Auth is a blocking popup (not a route): show it whenever the event
  // requires login and the user hasn't verified yet.
  const needsAuth =
    config !== undefined && !!config?.dataCollectionEnabled && !verified

  // Apply the active design's font across the whole event experience (gallery,
  // about, photographer, etc.) so all shared chrome matches the design.
  const galleryFontClass = getDesignFontClass(eventData?.galleryTheme)

  // Photographer can turn the guest gallery off. Treat null/undefined as live so
  // existing events stay open; only an explicit false closes it for guests.
  const galleryClosed =
    eventData && eventData !== "null" && eventData.galleryLive === false

  return (
    <div
      className={`min-h-screen h-screen max-h-screen overflow-y-scroll flex flex-col text-white bg-white dark:bg-black ${galleryFontClass}`}
    >
      <MetaTags />
      {galleryClosed ? (
        <GalleryUnavailable />
      ) : (
        <>
      <WhatsAppBanner />
      <Navbar />
      <SelectedPhotosNavbar />
      <Modals />
      <IndividualDownloadModal />
      <Routes>
        <Route path="/" element={<Error />} />
        <Route path="/:eventId" element={<GalleryRedirect />} />
        <Route path="/:eventId/auth" element={<AuthRedirect />} />
        <Route path="/:eventId/gallery" element={<Gallery />} />
        <Route path="/:eventId/physical-album" element={<PhysicalAlbum />} />
        <Route
          path="/:eventId/physical-album/:albumId"
          element={<PhysicalAlbumDetail />}
        />
        <Route path="/:eventId/about" element={<About />} />
        <Route path="/:eventId/file/:fileId" element={<FullScreen />} />
        <Route
          path="*"
          element={
            loading && eventData === "null" ? (
              <FullScreenLoader />
            ) : (
              <Error />
            )
          }
        />
      </Routes>
      <BottomBar />
      <FloatingUploadButton />
      {totalParts > 0 && (
        <div
          onClick={() => dispatch(setShowDownloadModal(true))}
          className="fixed bottom-3 right-3 md:bottom-8 md:right-8 flex justify-center items-center rounded-full cursor-pointer animate-bounce z-30"
        >
          <img
            src={downloaderGIF}
            alt="downloaderGIF"
            className="h-[65px] w-[65px] md:h-[80px] md:w-[80px] z-10"
          />
          <div className="bg-black/65 md:bg-black/55 h-[45px] w-[45px] md:h-[60px] md:w-[60px] rounded-full absolute" />
        </div>
      )}
      {needsAuth && <AuthModal />}
        </>
      )}
      <Toaster position="top-right" richColors closeButton />
    </div>
  )
}

export default App
